import { _callBackend } from './api';
import { processDocument, fileUpload } from '@/_api/client-upload';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import type {
  Question,
  Questionnaire,
  QuestionNote,
  EntityUser,
} from '@/_types/models/questionnaire';

// --------------------------------
// Questionnaires

export const markAsOpened = async ({
  id,
  companyId,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/questionnaires/mark_as_opened',
    'POST',
    { id, company_id: companyId },
    token,
  );

export const createCheckoutSession = async ({
  id,
  companyId,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
}>): APIResponsePromise<{ checkout_url: string }> =>
  await _callBackend(
    '/questionnaires/create_checkout_session',
    'POST',
    { id, company_id: companyId },
    token,
  );

export const verifyCheckoutSession = async ({
  companyId,
  questionnaireId,
  token,
}: APIRequest<{
  companyId: number;
  questionnaireId: number;
}>): APIResponsePromise<{ deposit_status: string; redirect_url: string }> =>
  await _callBackend(
    '/questionnaires/verify_checkout_session',
    'POST',
    {
      company_id: companyId,
      questionnaire_id: questionnaireId,
    },
    token,
  );

export const getClientQuestionnaires = async ({
  companyId,
  token,
}: APIRequest<{
  companyId: number;
}>): APIResponsePromise<Array<Questionnaire>> =>
  await _callBackend(
    `/questionnaires/get_client_questionnaires/?company_id=${companyId}`,
    'GET',
    null,
    token,
  );

export const getQuestionnaire = async ({
  companyId,
  questionnaireId,
  token,
}: APIRequest<{
  companyId: number;
  questionnaireId: number;
}>): APIResponsePromise<
  Questionnaire & { questionnaire_questions: Array<Question> }
> =>
  await _callBackend(
    `/questionnaires/get_questionnaire?company_id=${companyId}&id=${questionnaireId}`,
    'GET',
    null,
    token,
  );

export const getActiveEntityUsers = async ({
  companyId,
  token,
}: APIRequest<{
  companyId: number;
}>): APIResponsePromise<Array<EntityUser>> =>
  await _callBackend(
    `/questionnaires/get_active_entity_users?company_id=${companyId}`,
    'GET',
    null,
    token,
  );

export const answerQuestion = async ({
  companyId,
  answer,
  idOfDocMovedByUser,
  questionId,
  token,
  answerBy,
}: APIRequest<{
  companyId: number;
  answer: string;
  idOfDocMovedByUser?: number;
  questionId: number;
  answerBy?: string | null;
}>): APIResponsePromise<Question> =>
  await _callBackend(
    '/questionnaires/answer_question',
    'POST',
    {
      answer,
      answer_by: answerBy,
      id: questionId,
      id_of_doc_moved_by_user: idOfDocMovedByUser,
      company_id: companyId,
    },
    token,
  );

export const deleteQuestion = async ({
  companyId,
  questionId,
  questionnaireId,
  token,
}: APIRequest<{
  companyId: number;
  questionId: number;
  questionnaireId: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/questionnaires/delete_question',
    'POST',
    {
      company_id: companyId,
      id: questionId,
      questionnaire_id: questionnaireId,
    },
    token,
  );

export const saveSection = async ({
  companyId,
  customElement,
  id,
  markedCompleted,
  setId,
  token,
}: APIRequest<{
  companyId: number;
  customElement?: string;
  id: number;
  setId: number;
  markedCompleted: boolean;
}>): APIResponsePromise =>
  await _callBackend(
    '/questionnaires/save_section',
    'POST',
    {
      company_id: companyId,
      custom_element: customElement,
      id,
      set_id: setId,
      marked_completed: markedCompleted,
    },
    token,
  );

export const saveQuestionnaire = async ({
  companyId,
  id,
  params,
  token,
}: APIRequest<{
  companyId: number;
  id: number;
  params: any;
}>): APIResponsePromise =>
  await _callBackend(
    '/questionnaires/save_questionnaire',
    'POST',
    { company_id: companyId, id, ...params },
    token,
  );

// --------------------------------
// Notes

// TODO(API) @chris getting userId should happen in the backend
// TODO(API) @chris `removed` and `from_firm_user` are unnecessary
// TODO(API) @chris https://linear.app/soraban/issue/SOR-7979/stop-saving-client-name-in-questionnaire-comments-text
export const addNote = async ({
  questionId,
  text,
  userId,
  companyId,
  token,
}: APIRequest<{
  questionId: number;
  text: string;
  userId?: number;
  companyId: number;
}>): APIResponsePromise<QuestionNote> =>
  await _callBackend(
    '/questionnaires/add_comment',
    'POST',
    {
      company_id: companyId,
      from_firm_user: false,
      questionnaire_question_id: questionId,
      removed: false,
      text,
      user_id: userId,
    },
    token,
  );

// TODO(API) @chris should only need to pass commentId
export const deleteNote = async ({
  noteId,
  companyId,
  questionId,
  token,
}: APIRequest<{
  noteId: number;
  companyId?: number;
  questionId: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/questionnaires/toggle_comment_removal',
    'POST',
    {
      questionnaire_question_id: questionId,
      questionnaire_comment_id: noteId,
      company_id: companyId,
    },
    token,
  );

// --------------------------------
// Client Entries

export const addNewEntry = async ({
  companyId,
  questionId,
  token,
}: APIRequest<{
  companyId: number;
  questionId: number;
}>): APIResponsePromise<Array<Question>> =>
  await _callBackend(
    '/questionnaires/add_new_entry',
    'POST',
    { company_id: companyId, questionnaire_question_id: questionId },
    token,
  );

export const removeEntry = async ({
  questionnaireQuestionId,
  companyId,
  entryNumber,
  token,
}: APIRequest<{
  questionnaireQuestionId: number;
  companyId: number;
  entryNumber: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/questionnaires/remove_entry',
    'POST',
    {
      questionnaire_question_id: questionnaireQuestionId,
      company_id: companyId,
      entry_number: entryNumber,
    },
    token,
  );

// --------------------------------
// Signatures

export const removeSignature = async ({
  companyId,
  questionnaireId,
  signatureId,
  token,
}: APIRequest<{
  companyId: number;
  questionnaireId: number;
  signatureId: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/questionnaires/remove_signature',
    'POST',
    {
      company_id: companyId,
      questionnaire_id: questionnaireId,
      signature_id: signatureId,
    },
    token,
  );

export const saveSignature = async ({
  questionnaireId,
  companyId,
  signature,
  signatureId,
  token,
}: APIRequest<{
  questionnaireId: number;
  companyId: number;
  signature: string;
  signatureId: number;
}>): APIResponsePromise => {
  return await _callBackend(
    '/questionnaires/sign_agreement',
    'POST',
    {
      client_time: new Date().toISOString(),
      company_id: companyId,
      questionnaire_id: questionnaireId,
      signature,
      signature_id: signatureId,
      user_agent: window.navigator.userAgent,
    },
    token,
  );
};

export const addSignatureRequest = async ({
  questionnaireId,
  companyId,
  userId,
  sectionId,
  token,
}: APIRequest<{
  questionnaireId: number;
  companyId: number;
  userId: number;
  sectionId: number;
}>): APIResponsePromise => {
  return await _callBackend(
    '/questionnaires/add_signature_request',
    'POST',
    {
      company_id: companyId,
      questionnaire_id: questionnaireId,
      user_id: userId,
      section_id: sectionId,
    },
    token,
  );
};

export const removeSignatureRequest = async ({
  companyId,
  questionnaireId,
  signatureId,
  token,
}: APIRequest<{
  companyId: number;
  questionnaireId: number;
  signatureId: number;
}>): APIResponsePromise => {
  return await _callBackend(
    '/questionnaires/remove_signature_request',
    'POST',
    {
      company_id: companyId,
      questionnaire_id: questionnaireId,
      signature_id: signatureId,
    },
    token,
  );
};

export const uploadDocument = async ({
  companyId,
  description,
  documentName,
  documentType,
  file,
  path,
  questionnaireId,
  requiresSplitting,
  selectedOptions,
  tags,
  token,
}: APIRequest<{
  companyId: number;
  description: string;
  documentName: string;
  documentType?: string;
  file: File;
  path: string;
  questionnaireId: number;
  requiresSplitting?: boolean;
  selectedOptions: Array<string>;
  tags: Array<string>;
}>): APIResponsePromise => {
  // Get a temporary upload link
  const { upload_link, id } = await _callBackend(
    `/integrations/get_temporary_upload_link?company_id=${companyId}`,
    'GET',
    null,
    token,
  );

  // Upload file
  const url = new URL(upload_link);
  const isDropbox: boolean = url.hostname.includes('dropbox');
  await fileUpload(upload_link, isDropbox, file);

  // Process uploaded document
  const processed = await processDocument({
    companyId,
    description,
    documentName: documentName || file.name,
    documentType,
    id,
    path: path || '',
    requiresSplitting,
    questionnaireId,
    selectedOptions,
    tags,
    token,
  });

  return processed;
};

// TODO @chris maybe we should call answer here directly instead with `answer: ""`
export const deletePath = async ({
  companyId,
  path,
  token,
}: APIRequest<{
  companyId: number;
  path: string;
}>): APIResponsePromise =>
  await _callBackend(
    '/integrations/delete_path',
    'POST',
    { company_id: companyId, path },
    token,
  );

export const getExportedDocumentsList = async ({
  id,
  token,
}: APIRequest<{
  id: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/questionnaires/get_exported_documents_list?id=${id}`,
    'GET',
    null,
    token,
  );

// --------------------------------
// Submit

export const submitQuestionnaire = async ({
  questionnaireId,
  companyId,
  token,
}: APIRequest<{
  questionnaireId: number;
  companyId: number;
}>): APIResponsePromise<{
  questionnaire_id: number;
  hash_id: string;
  name: string;
}> =>
  await _callBackend(
    `/questionnaires/${questionnaireId}/submit_questionnaire`,
    'POST',
    { company_id: companyId },
    token,
  );

// --------------------------------
// Other

export const attachDocumentsToQuestionnaire = async ({
  companyId,
  extractedDocumentIds,
  questionnaireId,
  token,
}: APIRequest<{
  companyId: number;
  extractedDocumentIds: Array<number>;
  questionnaireId: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/questionnaires/${questionnaireId}/attach_to_questionnaire`,
    'POST',
    { company_id: companyId, extracted_document_ids: extractedDocumentIds },
    token,
  );

export const generateQuestionnaireLink = async ({
  companyId,
  questionnaireId,
  token,
}: APIRequest<{
  companyId: number;
  questionnaireId: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/questionnaires/${questionnaireId}/generate_questionnaire_link`,
    'POST',
    { company_id: companyId },
    token,
  );

export const downloadAttachedDocument = async ({
  questionId,
  token,
}: APIRequest<{
  questionId: number;
}>): APIResponsePromise<{ document: { data: { link: string } } }> =>
  await _callBackend(
    `/questionnaires/download_attached_document?id=${questionId}`,
    'POST',
    {},
    token,
  );
